import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FilePdfOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Form, Select, Col, Row } from 'antd';
import clsx from 'clsx';
import dayjs from 'dayjs';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { useNotify } from '@/hooks';
import { useCompany } from '@/hooks/queries/use-company';
import { API } from '@/services/api.service';
import { downloadQuestionnairePdf } from '@/services/companies.service';
import { QUERY_KEY } from '@/shared/constants';
import ClientInfoSection from './questionnaire/ClientInfoSection';
import DefinitionsSection from './questionnaire/DefinitionsSection';
import OwnersInfoSection from './questionnaire/OwnersInfoSection';
import OwnershipSection from './questionnaire/OwnershipSection';
import RepresentativesSection from './questionnaire/RepresentativesSection';
import ServicesSection from './questionnaire/ServicesSection';

const { Option } = Select;

export default function CompanyQuestionnaireForm({ initialValues: _initialValues, className }) {
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation(['forms', 'messages']);
  const notification = useNotify();
  const { companyId } = useParams();
  const queryClient = useQueryClient();
  const { setQueryParams } = useQueryParamsContext();

  // Language state - internal to questionnaire
  const [languageType, setLanguageType] = useState('en');

  // Checkbox states for conditional rendering
  const [isOtherChecked, setIsOtherChecked] = useState(false);
  const [isOtherSourceChecked, setIsOtherSourceChecked] = useState(false);
  const [businessWithForeignCountries, setBusinessWithForeignCountries] =
    useState(false);
  const [
    businessInActivitiesOtherThanPrimary,
    setBusinessInActivitiesOtherThanPrimary,
  ] = useState(false);
  const [isMainlyStateCompany, setIsMainlyStateCompany] = useState(false);
  const [
    securitiesListedOnSecurityMarket,
    setSecuritiesListedOnSecurityMarket,
  ] = useState(false);
  const [
    securitiesListedOnReputableMarket,
    setSecuritiesListedOnReputableMarket,
  ] = useState(false);

  // Upload files state
  const [ownershipChangeFiles, setOwnershipChangeFiles] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]); // Track files to delete from S3

  // PDF download loading state
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  // Fetch company data using shared hook (same cache as CompanyEdit)
  const { data: companyData, isLoading: companyLoading } =
    useCompany(companyId);

  // Get authorized people and owners from ownership structure
  const authorizedPeople = useMemo(() => {
    return (
      companyData?.ownershipStructure?.filter((person) => {
        const type =
          typeof person.type === 'string' ? person.type : person.type?.key;
        return type === 'authorized';
      }) || []
    );
  }, [companyData]);

  const owners = useMemo(() => {
    return (
      companyData?.ownershipStructure?.filter((person) => {
        const type =
          typeof person.type === 'string' ? person.type : person.type?.key;
        return type === 'owner' || type === 'founder';
      }) || []
    );
  }, [companyData]);

  // Owner states for checkboxes and inputs
  const [ownerStates, setOwnerStates] = useState([]);

  useEffect(() => {
    if (owners.length > 0) {
      const initialOwnerStates = owners.map((owner) => {
        const matchingOwner = companyData?.questionnaire?.owners?.find(
          (qOwner) =>
            qOwner.name === owner.name ||
            qOwner.personalNumber === (owner.personalNumber || owner.JMBG) ||
            qOwner.JMBG === (owner.personalNumber || owner.JMBG)
        );

        return {
          ...owner,
          ...matchingOwner,
          directOrIndirectOwns25:
            matchingOwner?.directOrIndirectOwns25 || false,
          directOrIndirectPredominant:
            matchingOwner?.directOrIndirectPredominant || false,
          indirectlyProvidesFunds:
            matchingOwner?.indirectlyProvidesFunds || false,
          entrepreneur: matchingOwner?.entrepreneur || false,
          founderInManagementOfOtherForeignEntity:
            matchingOwner?.founderInManagementOfOtherForeignEntity || false,
          founderInManagementOfOtherForeignEntityList:
            matchingOwner?.founderInManagementOfOtherForeignEntityList || '',
          individualDirectOwnerOfCompany:
            matchingOwner?.individualDirectOwnerOfCompany || false,
          nameOfLegalEntityOwnersOfCompany:
            matchingOwner?.nameOfLegalEntityOwnersOfCompany || '',
          officialOfDomesticState:
            matchingOwner?.officialOfDomesticState || false,
          officialOfForeignState:
            matchingOwner?.officialOfForeignState || false,
          officialOfInternationalOrganization:
            matchingOwner?.officialOfInternationalOrganization || false,
        };
      });
      setOwnerStates(initialOwnerStates);
    }
  }, [owners, companyData?.questionnaire]);

  // Set form initial values
  useEffect(() => {
    if (companyData) {
      const questionnaire = companyData.questionnaire || {};

      form.setFieldsValue({
        ...questionnaire,
        name: companyData.name || questionnaire.name,
        generalData: {
          ...companyData.generalData,
          city: questionnaire.city || companyData.generalData?.city,
          pib: questionnaire.pib || companyData.generalData?.pib,
          code: questionnaire.code || companyData.generalData?.code,
          address: questionnaire.address || companyData.generalData?.address,
        },
        accountingServices: questionnaire.accountingServices ?? true,
        payrollProcessing: questionnaire.payrollProcessing ?? false,
        isOtherChecked: questionnaire.isOtherChecked ?? false,
        generatedFromBusinessActivities:
          questionnaire.generatedFromBusinessActivities ?? true,
        donationsAndSponsorships:
          questionnaire.donationsAndSponsorships ?? false,
        isOtherSourceChecked: questionnaire.isOtherSourceChecked ?? false,
        isStateCompany: questionnaire.isStateCompany ?? false,
        isMainlyStateCompany: questionnaire.isMainlyStateCompany ?? false,
        anyOwnerOffShore: questionnaire.anyOwnerOffShore ?? false,
        legalEntityWithComplexOwnership:
          questionnaire.legalEntityWithComplexOwnership ?? false,
        isOwnerForeignLegalEntity:
          questionnaire.isOwnerForeignLegalEntity ?? false,
        changesInActualOwnersSinceLastSubmission:
          questionnaire.changesInActualOwnersSinceLastSubmission ?? false,
      });

      // Set conditional states
      setIsOtherChecked(questionnaire.isOtherChecked ?? false);
      setIsOtherSourceChecked(questionnaire.isOtherSourceChecked ?? false);
      setBusinessWithForeignCountries(
        questionnaire.businessWithForeignCountries ?? false
      );
      setBusinessInActivitiesOtherThanPrimary(
        questionnaire.businessInActivitiesOtherThanPrimary ?? false
      );
      setIsMainlyStateCompany(questionnaire.isMainlyStateCompany ?? false);
      setSecuritiesListedOnSecurityMarket(
        questionnaire.securitiesListedOnSecurityMarket ?? false
      );
      setSecuritiesListedOnReputableMarket(
        questionnaire.securitiesListedOnReputableMarket ?? false
      );
    }
  }, [companyData, form]);

  // Handle owner checkbox changes
  const handleCheckboxChange = (index, field, value) => {
    setOwnerStates((prevStates) =>
      prevStates.map((state, i) =>
        i === index ? { ...state, [field]: value } : state
      )
    );
  };

  // Handle owner input changes
  const handleInputChange = (index, field, value) => {
    setOwnerStates((prevStates) =>
      prevStates.map((state, i) =>
        i === index ? { ...state, [field]: value } : state
      )
    );
  };

  // Handle file removal - mark for deletion instead of deleting immediately
  const handleFileRemove = (fileKey, _file) => {
    setFilesToDelete((prev) => [...prev, fileKey]);
  };

  // Submit mutation
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (payload) => {
      return await API.companies.createQuestionnaire(payload);
    },
    onSuccess: (response) => {
      notification.success({
        message: t('success', { ns: 'messages' }),
        description: response.message,
      });

      // Invalidate company query to trigger refetch
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.company, companyId],
      });

      // Navigate to general-data tab
      setQueryParams({ route: 'general-data' });

      // Scroll to top after navigation
      setTimeout(() => {
        const scrollContainer = document.querySelector('.with-y-overflow');
        if (scrollContainer) {
          scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 200);
    },
    onError: (error) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: error.message || 'Failed to submit questionnaire',
      });
    },
  });

  const onFinish = async (values) => {
    const payload = {
      ...values,
      company: companyId,
      // Include disabled fields from companyData
      name: companyData.name,
      city: companyData.generalData?.city,
      pib: companyData.generalData?.pib,
      code: companyData.generalData?.code,
      isOtherChecked,
      isOtherSourceChecked,
      businessWithForeignCountries,
      businessInActivitiesOtherThanPrimary,
      isMainlyStateCompany,
      securitiesListedOnSecurityMarket,
      securitiesListedOnReputableMarket,
      owners: ownerStates.map((state) => ({
        name: state.name,
        personalNumber: state.personalNumber || state.JMBG,
        JMBG: state.JMBG || state.personalNumber,
        dateOfBirth: state.dateOfBirth,
        placeOfBirth: state.placeOfBirth,
        countryOfBirth: state.countryOfBirth,
        addressOfResidence: state.addressOfResidence,
        placeOfResidence: state.placeOfResidence,
        countryOfResidence: state.countryOfResidence,
        percentageOfOwnership: state.percentageOfOwnership,
        directOrIndirectOwns25: state.directOrIndirectOwns25,
        directOrIndirectPredominant: state.directOrIndirectPredominant,
        indirectlyProvidesFunds: state.indirectlyProvidesFunds,
        entrepreneur: state.entrepreneur,
        founderInManagementOfOtherForeignEntity:
          state.founderInManagementOfOtherForeignEntity,
        founderInManagementOfOtherForeignEntityList:
          state.founderInManagementOfOtherForeignEntityList,
        individualDirectOwnerOfCompany: state.individualDirectOwnerOfCompany,
        nameOfLegalEntityOwnersOfCompany:
          state.nameOfLegalEntityOwnersOfCompany,
        officialOfDomesticState: state.officialOfDomesticState,
        officialOfForeignState: state.officialOfForeignState,
        officialOfInternationalOrganization:
          state.officialOfInternationalOrganization,
      })),
      filesToDelete, // S3 keys to delete
    };

    // Create FormData for file upload
    const formData = new FormData();

    // Append each field as JSON string (matching backend parseMultipartFormBodyHelper)
    for (const [key, value] of Object.entries(payload)) {
      formData.append(key, JSON.stringify(value));
    }

    // Append files if any
    if (ownershipChangeFiles && ownershipChangeFiles.length > 0) {
      ownershipChangeFiles.forEach((file, index) => {
        formData.append(
          'ownershipChangeFiles',
          file,
          file.name || `document-${index}`
        );
      });
    }

    await mutateAsync(formData);
  };

  // Handle PDF download
  const handleDownloadPdf = async () => {
    setIsDownloadingPdf(true);
    try {
      await downloadQuestionnairePdf(companyId, languageType);
      notification.success({
        message: t('success', { ns: 'messages' }),
        description: 'PDF downloaded successfully',
      });
    } catch (error) {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: error.message || 'Failed to download PDF',
      });
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // Local translation helper that uses languageType instead of global i18n.language
  // Use i18n.t() with lng parameter to get translations for specific language
  const qt = useCallback(
    (key) => {
      return i18n.t(key, { lng: languageType, ns: 'forms' });
    },
    [languageType, i18n]
  );

  // Calculate update date
  const updateDate = companyData?.questionnaire?.updatedAt
    ? dayjs(companyData.questionnaire.updatedAt).format('DD-MM-YYYY HH:mm')
    : companyData?.questionnaire?.createdAt
      ? dayjs(companyData.questionnaire.createdAt).format('DD-MM-YYYY HH:mm')
      : qt('questionnaire.notSubmitted');

  const isNotSubmitted = updateDate === qt('questionnaire.notSubmitted');

  // Language options mapping - using translations that update with languageType
  const languageOptions = {
    en: qt('english'),
    sr: qt('serbian'),
    ru: qt('russian'),
  };

  // Change language handler - local to questionnaire form only
  const handleLanguageChange = async (value) => {
    // Force reload the language resources to ensure fresh translations
    await i18n.loadLanguages(value);
    setLanguageType(value);
  };

  if (companyLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Form
      form={form}
      name="company_questionnaire_form"
      layout="vertical"
      onFinish={onFinish}
      autoComplete="off"
      className={clsx('form p-4', className)}
      style={{ width: '100%', marginTop: '15px' }}
    >
      {/* Header with Last Update and Language Selector */}
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: '20px' }}
      >
        <Col>
          <p
            style={{
              margin: 0,
              color: isNotSubmitted ? 'red' : '#2b4a1d',
              fontWeight: 'bold',
              fontSize: '14px',
            }}
          >
            {qt('questionnaire.lastUpdate')}&nbsp;
            <span
              style={{
                backgroundColor: isNotSubmitted ? '#fdecea' : '#e6f7e9',
                padding: '4px 8px',
                borderRadius: '4px',
                display: 'inline-block',
              }}
            >
              {updateDate}
            </span>
          </p>
        </Col>
        <Col>
          <span style={{ marginRight: '10px' }}>
            {qt('questionnaire.language')}
          </span>
          <Select
            style={{ width: '120px' }}
            value={languageType}
            onChange={handleLanguageChange}
          >
            {Object.entries(languageOptions).map(([value, label]) => (
              <Option key={value} value={value}>
                {label}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      {/* PDF Download Button */}
      {companyData?.questionnaire && (
        <div style={{ textAlign: 'right', marginBottom: '20px' }}>
          <Button
            type="default"
            icon={
              <FilePdfOutlined style={{ fontSize: '20px', color: 'red' }} />
            }
            size="large"
            style={{ height: '50px' }}
            onClick={handleDownloadPdf}
            loading={isDownloadingPdf}
            disabled={isDownloadingPdf}
          >
            {qt('questionnaire.downloadPdf')}
          </Button>
        </div>
      )}

      {/* All Sections */}
      <ClientInfoSection companyData={companyData} qt={qt} />

      <RepresentativesSection authorizedPeople={authorizedPeople} qt={qt} />

      <ServicesSection
        isOtherChecked={isOtherChecked}
        setIsOtherChecked={setIsOtherChecked}
        isOtherSourceChecked={isOtherSourceChecked}
        setIsOtherSourceChecked={setIsOtherSourceChecked}
        businessWithForeignCountries={businessWithForeignCountries}
        setBusinessWithForeignCountries={setBusinessWithForeignCountries}
        businessInActivitiesOtherThanPrimary={
          businessInActivitiesOtherThanPrimary
        }
        setBusinessInActivitiesOtherThanPrimary={
          setBusinessInActivitiesOtherThanPrimary
        }
        qt={qt}
      />

      <OwnershipSection
        isMainlyStateCompany={isMainlyStateCompany}
        setIsMainlyStateCompany={setIsMainlyStateCompany}
        securitiesListedOnSecurityMarket={securitiesListedOnSecurityMarket}
        setSecuritiesListedOnSecurityMarket={
          setSecuritiesListedOnSecurityMarket
        }
        securitiesListedOnReputableMarket={securitiesListedOnReputableMarket}
        setSecuritiesListedOnReputableMarket={
          setSecuritiesListedOnReputableMarket
        }
        qt={qt}
      />

      <OwnersInfoSection
        owners={owners}
        ownerStates={ownerStates}
        handleCheckboxChange={handleCheckboxChange}
        handleInputChange={handleInputChange}
        qt={qt}
        ownershipChangeFiles={ownershipChangeFiles}
        setOwnershipChangeFiles={setOwnershipChangeFiles}
        handleFileRemove={handleFileRemove}
        language={languageType}
        questionnaireDocs={companyData?.questionnaire?.questionnaireDocs || []}
      />

      <DefinitionsSection qt={qt} />

      {/* Submit Button */}
      <div style={{ justifySelf: 'end', marginTop: '30px' }}>
        <Button
          type="primary"
          htmlType="submit"
          loading={isPending}
          disabled={isPending}
          size="large"
          className="btn-primary"
        >
          {qt('questionnaire.submit')}
        </Button>
      </div>
    </Form>
  );
}
